import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NotificationContext } from "../../context/NotificationContext";
import { AuthContext } from "../../context/AuthContext";  // Import the AuthContext
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Button,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { API } from "../../api/axios"; // Make sure API is correctly imported
import "./NotificationBell.css";

const NotificationBell = () => {
  const {
    notifications = [],
    unreadCount = 0,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } =
    useContext(NotificationContext) || {};
  const { userId, token, role: authRole } = useContext(AuthContext); // Use AuthContext to get user data
  const [hasSubscription, setHasSubscription] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [displayNotifications, setDisplayNotifications] = useState([]);
  const navigate = useNavigate();

  const userRole = authRole || localStorage.getItem("role");

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!userId || !token) return;

      try {
        const response = await API.get(`/payments/payby-details/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success && Array.isArray(response.data.result) && response.data.result.length > 0) {
          setHasSubscription(true);
        }
      } catch (error) {
        console.error("Error fetching subscription data:", error);
      }
    };

    fetchSubscriptionStatus();
  }, [userId, token]);

  useEffect(() => {
    if (Array.isArray(notifications)) setDisplayNotifications(notifications);
  }, [notifications]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification) => {
    try {
      if (!notification.isRead && typeof markAsRead === "function") {
        markAsRead(notification.id);
      }

      // Check if user is not subscribed and role is either "crew" or "actor"
      if (!hasSubscription && (userRole === "crew" || userRole === "actor")) {
        navigate("/premiumFeature");  // Redirect to premium feature if no subscription
      } else {
        // Handle other notification actions (navigate if needed)
        // if (notification.actionUrl) navigate(notification.actionUrl);
      }
    } catch (error) {
      console.error("Error handling notification click:", error);
    } finally {
      handleClose();
    }
  };

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    if (typeof markAllAsRead === "function") {
      try {
        markAllAsRead();
      } catch (error) {
        console.error("Error marking all as read:", error);
      }
    }
  };

  const handleDeleteNotification = async (event, notificationId) => {
    event.stopPropagation();
    if (typeof deleteNotification === "function") {
      await deleteNotification(notificationId);
    }
  };

  const handleViewAll = (e) => {
    e.stopPropagation();
    navigate("/notifications");
    handleClose();
  };

  const getNotificationColor = (type) => {
    return {
      message: "#2196F3",
      invitation: "#FF9800",
      application: "#4CAF50",
      approval: "#8BC34A",
      rejection: "#F44336",
    }[type] || "#757575";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Just now" : date.toLocaleString();
  };

  return (
    <>
      <Tooltip title={`${unreadCount} new notifications`} arrow>
        <IconButton
          color="inherit"
          onClick={handleOpen}
          className="notification-bell-button"
          aria-label="notifications"
          aria-controls={anchorEl ? "notification-menu" : undefined}
          aria-haspopup="true"
          id={anchorEl ? "notification-button" : undefined}
          sx={{
            position: "relative",
            fontSize: "24px",
            "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
          }}
        >
          <Badge
            badgeContent={unreadCount || 0}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#f44336",
                color: "#fff",
              },
            }}
          >
            <NotificationsIcon sx={{ fontSize: "28px" }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock
        MenuListProps={{ "aria-labelledby": "notification-button", role: "listbox" }}
        PaperProps={{
          sx: {
            maxHeight: 500,
            width: 400,
            maxWidth: "90vw",
            boxShadow: "0 5px 40px rgba(0, 0, 0, 0.16)",
            borderRadius: "8px",
            py: 0,
          },
          elevation: 3,
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, m: 0 }}>
              Notifications
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {unreadCount > 0 && markAllAsRead && (
                <Button
                  size="small"
                  onClick={handleMarkAllAsRead}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    color: "#2196F3",
                    padding: "4px 8px",
                    "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
                  }}
                >
                  Mark all as read
                </Button>
              )}
              <IconButton
                aria-label="close notifications"
                onClick={handleClose}
                size="small"
                sx={{ color: "#666", p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 400, overflowY: "auto", overflowX: "hidden" }}>
          {displayNotifications.length > 0 ? (
            <>
              {displayNotifications.slice(0, 10).map((notification, index) => (
                <MenuItem
                  key={notification.id || index}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.isRead ? "transparent" : "#fafafa",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    py: 1.5,
                    px: 2,
                    borderLeft: `4px solid ${getNotificationColor(notification.type)}`,
                    transition: "all 0.2s ease",
                    "&:hover": { backgroundColor: "#f0f0f0", paddingLeft: "18px" },
                    minHeight: "auto",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          fontWeight: notification.isRead ? 400 : 600,
                          lineHeight: 1.4,
                          flex: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {notification.message || "Notification"}
                      </Typography>
                      {!notification.isRead && (
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "#2196F3",
                            mt: 0.5,
                          }}
                        />
                      )}
                      <IconButton
                        aria-label="delete notification"
                        size="small"
                        onClick={(event) =>
                          handleDeleteNotification(event, notification.id)
                        }
                        sx={{
                          color: "#6e0506",
                          p: 0.25,
                          flexShrink: 0,
                          "&:hover": { backgroundColor: "rgba(110, 5, 6, 0.1)" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" sx={{ color: "#999", fontSize: "11px", mt: 0.5 }}>
                      {formatDate(notification.createdAt)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                onClick={handleViewAll}
                sx={{
                  justifyContent: "center",
                  py: 1.5,
                  color: "#2196F3",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                View All Notifications
              </MenuItem>
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <Box sx={{ textAlign: "center" }}>
                <NotificationsIcon sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
                <Typography variant="body2" sx={{ color: "#999" }}>
                  No notifications yet
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
